import { useRef, useState, useEffect, useCallback } from "react";
import { Info } from "lucide-react";

const HOVER_DELAY_MS = 400;
const POPOVER_WIDTH = 320;
const POPOVER_MARGIN = 12;

/**
 * Info icon + hover/tap popover that shows a template's description and
 * usage-scenario copy without opening the template. Renders NOTHING when
 * both fields are empty — the icon itself is opt-in per template.
 *
 * - Desktop: 400 ms hover-intent open, immediate dismiss on mouse-leave.
 * - Touch: tap to toggle; outside-click dismisses.
 * - Positioning: below the icon by default, flips above and shifts
 *   horizontally to keep the box inside the viewport.
 */
export function TemplateInfoPopover({ template, testIdSuffix = "" }) {
    const description = (template?.description || "").trim();
    const usageScenario = (template?.usage_scenario || "").trim();
    const hasContent = !!(description || usageScenario);

    const wrapRef = useRef(null);
    const popRef = useRef(null);
    const openTimerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, placement: "bottom" });

    const computePosition = useCallback(() => {
        const anchor = wrapRef.current;
        if (!anchor) return;
        const rect = anchor.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        // Approximate popover height for the initial decision; the actual
        // element's height is used on the second measurement pass below.
        const approxHeight = popRef.current?.offsetHeight || 160;
        const spaceBelow = vh - rect.bottom;
        const spaceAbove = rect.top;
        const placeAbove = spaceBelow < approxHeight + POPOVER_MARGIN
                        && spaceAbove > spaceBelow;

        const top = placeAbove
            ? Math.max(POPOVER_MARGIN, rect.top - approxHeight - 8)
            : rect.bottom + 8;

        // Prefer aligning the popover's left with the icon, but clamp so
        // the whole box stays visible.
        let left = rect.left;
        if (left + POPOVER_WIDTH + POPOVER_MARGIN > vw) {
            left = vw - POPOVER_WIDTH - POPOVER_MARGIN;
        }
        if (left < POPOVER_MARGIN) left = POPOVER_MARGIN;

        setPos({ top, left, placement: placeAbove ? "top" : "bottom" });
    }, []);

    useEffect(() => {
        if (!open) return;
        // Two-pass placement: first paint sets initial coords, second pass
        // uses the real rendered height. Keeps it accurate for long copy.
        computePosition();
        const raf = requestAnimationFrame(computePosition);
        const onScrollOrResize = () => computePosition();
        window.addEventListener("scroll", onScrollOrResize, true);
        window.addEventListener("resize", onScrollOrResize);
        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("scroll", onScrollOrResize, true);
            window.removeEventListener("resize", onScrollOrResize);
        };
    }, [open, computePosition]);

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (wrapRef.current?.contains(e.target)) return;
            if (popRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("touchstart", onDocClick);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("touchstart", onDocClick);
        };
    }, [open]);

    useEffect(() => () => {
        if (openTimerRef.current) clearTimeout(openTimerRef.current);
    }, []);

    const scheduleOpen = () => {
        if (openTimerRef.current) clearTimeout(openTimerRef.current);
        openTimerRef.current = setTimeout(() => setOpen(true), HOVER_DELAY_MS);
    };
    const cancelOpen = () => {
        if (openTimerRef.current) clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
    };
    const closeNow = () => {
        cancelOpen();
        setOpen(false);
    };

    if (!hasContent) return null;

    const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        cancelOpen();
        setOpen((v) => !v);
    };

    return (
        <span
            ref={wrapRef}
            data-testid={`template-info-anchor${testIdSuffix ? `-${testIdSuffix}` : ""}`}
            onMouseEnter={scheduleOpen}
            onMouseLeave={closeNow}
            onFocus={scheduleOpen}
            onBlur={closeNow}
            style={{ display: "inline-flex", alignItems: "center", position: "relative" }}
        >
            <button
                type="button"
                onClick={handleClick}
                aria-label="Show template description and usage scenario"
                aria-expanded={open}
                data-testid={`template-info-btn${testIdSuffix ? `-${testIdSuffix}` : ""}`}
                style={{
                    background: "transparent",
                    border: "none",
                    padding: 2,
                    borderRadius: 999,
                    cursor: "pointer",
                    color: open ? "var(--tms-navy)" : "var(--tms-faint)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    lineHeight: 0,
                }}
            >
                <Info size={14} />
            </button>

            {open && (
                <div
                    ref={popRef}
                    role="tooltip"
                    data-testid={`template-info-popover${testIdSuffix ? `-${testIdSuffix}` : ""}`}
                    onMouseEnter={cancelOpen}
                    onMouseLeave={closeNow}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: "fixed",
                        top: pos.top,
                        left: pos.left,
                        width: POPOVER_WIDTH,
                        maxWidth: `calc(100vw - ${POPOVER_MARGIN * 2}px)`,
                        background: "#fff",
                        border: "1px solid var(--tms-border)",
                        borderRadius: 12,
                        boxShadow: "0 12px 32px rgba(15,25,45,0.18)",
                        padding: 14,
                        zIndex: 80,
                        color: "var(--tms-text)",
                        fontSize: 12.5,
                        lineHeight: 1.55,
                    }}
                >
                    {description && (
                        <div style={{ marginBottom: usageScenario ? 10 : 0 }}>
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "var(--tms-muted)",
                                    marginBottom: 4,
                                }}
                            >
                                Description
                            </div>
                            <div style={{ color: "var(--tms-navy)" }}>{description}</div>
                        </div>
                    )}
                    {usageScenario && (
                        <div>
                            <div
                                style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "var(--tms-muted)",
                                    marginBottom: 4,
                                }}
                            >
                                When to use
                            </div>
                            <div style={{ color: "var(--tms-navy)" }}>{usageScenario}</div>
                        </div>
                    )}
                </div>
            )}
        </span>
    );
}

export default TemplateInfoPopover;
